import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import { HELP_URL } from '@diritto/common/constants/url';
import { ContestId } from '@diritto/common/content/contest.constants';
import ScrollToggleButton from '@/app/(appHeader)/promotions/2025-pick-and-choo/vote/components/ScrollBottomButton';
import { usePromotionEligibilityCheck } from '@/app-components/promotions/UsePromotionEligibilityCheck';
import { StampDay, StampToday } from '@/app/(appHeader)/promotions/2025-pick-and-choo/vote/components/stamps';
import { isPC, PCOffset } from '@/constants/layout';
import { useAuthState } from '@/contexts/AuthProvider';
import { useGetContestVoteCountQuery } from '@/queries/contests/useGetContestVoteCountQuery';
import { useGetContestVotePeriodQuery } from '@/queries/contests/useGetContestVotePeriodQuery';
import { resizeMap, useResponsiveImageMap } from '@/ui-hooks/useResponsiveImageMap';
import { useViewPort } from '@/ui-hooks/useViewPort';
import { configure } from '@/utils/configure';

export function VoteTab({ currentYyyymmdd }: { currentYyyymmdd: string }) {
  const { width: screenWidth } = useViewPort();
  const { isLoggedIn } = useAuthState();
  const { data: votePeriod } = useGetContestVotePeriodQuery(ContestId.Contest2025);
  const { data: voteCountData } = useGetContestVoteCountQuery(ContestId.Contest2025);

  const { handleRidiSSoLink, handleUserCertification, isUserCertificated, ridiSsoLinked } =
    usePromotionEligibilityCheck();

  const contentWidth = useMemo(
    () => (screenWidth > PCOffset ? Math.min(screenWidth, 730) : screenWidth),
    [screenWidth],
  );

  const scale = useMemo(() => contentWidth / 730, [contentWidth]);

  const HELP_QNA_URL = `${HELP_URL}/hc/ko/articles/11723419022351-판타지-픽앤츄`;
  const VOTE_URL = '/promotions/2025-pick-and-choo/vote?tab=content';

  useResponsiveImageMap('image2', 'image-map2');
  useResponsiveImageMap('image3', 'image-map3');
  useResponsiveImageMap('image5', 'image-map5');

  const voteDates = useMemo(() => {
    if (!votePeriod) {
      return [];
    }
    const start = DateTime.fromJSDate(new Date(votePeriod.startAt)).setZone('Asia/Seoul');
    const end = DateTime.fromJSDate(new Date(votePeriod.endAt)).setZone('Asia/Seoul');
    return Array.from({ length: end.diff(start, 'days').days + 1 }, (_, i) =>
      start.plus({ days: i }).toFormat('yyyy-MM-dd'),
    ).slice(0, 7);
  }, [votePeriod]);

  useEffect(() => {
    resizeMap('image2', 'image-map2');
    resizeMap('image3', 'image-map3');
    resizeMap('image5', 'image-map5');
  }, []);

  const renderedVoteCountData = useMemo(
    () =>
      voteDates.map((date, index) => {
        const voteCount = voteCountData?.count[date]?.votedContentIds.length || 0;
        if (date > currentYyyymmdd) {
          return { day: index + 1, status: 'normal', isToday: false, date };
        }
        if (date === currentYyyymmdd) {
          if (voteCount < 3) {
            return { day: index + 1, status: 'normal', isToday: true, voteCount, date };
          }
          return { day: index + 1, status: 'success', isToday: true, voteCount, date };
        }
        if (date < currentYyyymmdd) {
          if (voteCount < 3) {
            return { day: index + 1, status: 'fail', isToday: false, date };
          }
          return { day: index + 1, status: 'success', isToday: false, date };
        }

        return { day: index + 1, status: 'normal', isToday: false, date };
      }),
    [currentYyyymmdd, voteCountData?.count, voteDates],
  );

  const voteSuccessDays = useMemo(
    () =>
      renderedVoteCountData.reduce((acc, cur) => {
        if (cur.status === 'success') {
          return acc + 1;
        }
        return acc;
      }, 0),
    [renderedVoteCountData],
  );

  const participatesPointCalc = useMemo(
    () => (totalCount: number) => {
      switch (true) {
        case totalCount > 0 && totalCount <= 2:
          return '300';
        case totalCount > 2 && totalCount <= 4:
          return '500';
        case totalCount > 4 && totalCount <= 6:
          return '700';
        case totalCount >= 7:
          return '1,000';
        default:
          return '0';
      }
    },
    [],
  );

  return (
    <div className="flex flex-col justify-center w-full h-auto bg-white min-h-[100vh] relative">
      <div className="flex justify-center w-full bg-[#E2DAF7] relative">
        <img
          alt="event"
          id="image2"
          src={`${configure.staticPrefix}/images/contests/3/vote/event/2.png`}
          className="w-full h-auto"
        />
        <div
          className="absolute w-[470px] h-[123px] rounded-[8px]"
          aria-details="디리토 본인 인증"
          onClick={isUserCertificated ? undefined : handleUserCertification}
          style={{
            top: `${2602 * scale}px`,
            left: `${130 * scale}px`,
            width: `${470 * scale}px`,
            height: `${123 * scale}px`,
            backgroundImage: `url(${configure.staticPrefix}/images/contests/3/vote/icons/dimbutton.svg)`,
            backgroundSize: '100% 100%',
            opacity: isUserCertificated ? 1 : 0,
          }}
        />
        <div
          className="absolute w-[470px] h-[123px] rounded-[8px]"
          aria-details="리디 계정 연결하기"
          onClick={ridiSsoLinked ? undefined : handleRidiSSoLink}
          style={{
            top: `${2733 * scale}px`,
            left: `${130 * scale}px`,
            width: `${470 * scale}px`,
            height: `${123 * scale}px`,
            backgroundImage: `url(${configure.staticPrefix}/images/contests/3/vote/icons/dimbutton.svg)`,
            backgroundSize: '100% 100%',
            opacity: ridiSsoLinked ? 1 : 0,
          }}
        />
      </div>
      <div className="flex justify-center w-full bg-[#322E36] relative">
        <img
          alt="event"
          id="image3"
          useMap="#image-map3"
          src={`${configure.staticPrefix}/images/contests/3/vote/event/3-1.png`}
          className="w-full h-auto"
        />
        <div
          className="absolute left-0 top-[393px] w-full"
          style={{ top: `${393 * scale}px`, padding: `0 ${94 * scale}px` }}>
          <style jsx>{`
            body div {
              -webkit-transform: translate3d(0, 0, 0);
            }
            @keyframes marquee {
              0% {
                transform: translateX(100%);
              }
              100% {
                transform: translateX(-100%);
              }
            }
          `}</style>
          <div className="w-full grid grid-cols-3 gap-1 pc:gap-y-3 gap-y-1">
            {renderedVoteCountData.map(item =>
              item.isToday && isLoggedIn ? (
                <StampToday
                  key={item.day}
                  day={item.day as 1 | 3 | 5 | 2 | 7 | 4 | 6}
                  status={item.status as 'normal' | 'success' | 'fail'}
                  voteCount={item.voteCount as 0 | 1 | 2 | 3}
                />
              ) : (
                <StampDay
                  key={item.day}
                  day={item.day as 1 | 3 | 5 | 2 | 7 | 4 | 6}
                  isToday={item.isToday}
                  status={isLoggedIn ? (item.status as 'normal' | 'success' | 'fail') : 'normal'}
                />
              ),
            )}
          </div>
        </div>
        <StyledScaleWrapper scale={scale} className="absolute left-0 w-full">
          <map name="image-map3">
            <area
              target="_self"
              alt="투표하러가기"
              title="투표하러가기"
              href={VOTE_URL}
              coords="143,1384,970,1564"
              shape="rect"
            />
          </map>
        </StyledScaleWrapper>
      </div>
      {isLoggedIn && (
        <div className="flex justify-center w-full bg-[#322E36] relative">
          <img
            alt="event"
            src={`${configure.staticPrefix}/images/contests/3/vote/event/3-2.png`}
            className="w-full h-auto"
          />
          <div
            className="absolute left-0 w-full flex flex-row items-center"
            style={{ top: `${256 * scale}px`, padding: `0 ${86 * scale}px` }}>
            <div className="flex w-full justify-center md:text-[40px] text-[24px] font-bold font-gray-500 text-center">
              {`${voteSuccessDays}`}
            </div>
            <div className="flex w-full justify-center md:text-[40px] text-[24px] font-bold font-gray-500 text-center">
              {participatesPointCalc(voteSuccessDays)}
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-center w-full bg-white">
        <img
          alt="event"
          src={`${configure.staticPrefix}/images/contests/3/vote/event/4.png`}
          className="w-full h-auto"
        />
      </div>
      <div className="flex justify-center w-full bg-white">
        <img
          alt="event"
          id="image5"
          useMap="#image-map5"
          src={`${configure.staticPrefix}/images/contests/3/vote/event/5.png?01`}
          className="w-full h-auto"
        />
        <map name="image-map5">
          <area
            target="_self"
            alt="프로그램 안내 및 상세 FAQ"
            title="프로그램 안내 및 상세 FAQ"
            href={HELP_QNA_URL}
            coords="110,2440,990,2614"
            shape="rect"
          />
        </map>
      </div>
      <ScrollToggleButton />
    </div>
  );
}

export const StyledScaleWrapper = styled.div<{ scale: number }>`
  transform: scale(${({ scale = 0.3 }) => scale});
  transition: width 0.3s ease-in-out;

  @media (${isPC}) {
    transform: scale(1);
  }
`;
